import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import Phase2TrialScoreboard from './Phase2TrialScoreboard'
import { formatDeciderTime } from './trackRecord/Phase2DeciderNote'
import type {
  Phase2Comparison,
  Phase2Decider,
  Phase2RowStats,
  Phase2Table,
} from '../types/phase2Comparison'

/**
 * Settings → Engine, admins only: the Phase 2 model against the Phase 1 rule
 * since the first model scored a setup (backend services/phase2_decider).
 * Every closed signal and resolved rejected setup carries both verdicts, so
 * each lands in a "would keep" or "would reject" row for each. Gross R,
 * before fees. Rendered as the server sends it; never part of the record.
 */

export const NO_MODEL_TEXT = 'No model yet — training starts at 200 usable closes.'

const WHO: Record<Phase2Decider, string> = { model: 'Phase 2 model', rule: 'Phase 1 rule' }
const SHORT: Record<Phase2Decider, string> = { model: 'Model', rule: 'Rule' }

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`)
const signedR = (v: number | null) => (v === null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`)

const day = (iso: string) => (formatDeciderTime(iso) ?? iso).replace(/ \d{2}:\d{2} UTC$/, '')

const StatRow = ({ label, s, testId }: { label: string; s: Phase2RowStats; testId: string }) => (
  <tr className="border-t border-gray-100" data-testid={testId}>
    <td className="px-2 py-1.5 text-gray-700">{label}</td>
    <td className="px-2 py-1.5 text-right tabular-nums">{s.n}</td>
    {s.too_few ? (
      <td colSpan={2} className="px-2 py-1.5 text-right text-xs text-amber-700">
        too few to judge
      </td>
    ) : (
      <>
        <td className="px-2 py-1.5 text-right tabular-nums">{pct(s.win_rate)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{signedR(s.avg_gross_r)}</td>
      </>
    )}
  </tr>
)

const VerdictTable = ({
  table,
  inCharge,
  testId,
}: {
  table: Phase2Table
  inCharge: Phase2Decider | null
  testId: string
}) => (
  <table className="w-full text-sm" data-testid={testId}>
    <thead>
      <tr className="text-xs text-gray-500">
        <th className="px-2 py-1 text-left font-medium">Verdict</th>
        <th className="px-2 py-1 text-right font-medium">n</th>
        <th className="px-2 py-1 text-right font-medium">Win rate</th>
        <th className="px-2 py-1 text-right font-medium">Avg R (gross)</th>
      </tr>
    </thead>
    <tbody>
      {(['model', 'rule'] as const).map((who) => {
        const mark = inCharge === who ? ' (in charge)' : ''
        return [
          <StatRow
            key={`${who}-keep`}
            label={`${SHORT[who]}${mark} — would keep`}
            s={table[who].keep}
            testId={`${testId}-${who}-keep`}
          />,
          <StatRow
            key={`${who}-reject`}
            label={`${SHORT[who]}${mark} — would reject`}
            s={table[who].reject}
            testId={`${testId}-${who}-reject`}
          />,
        ]
      })}
    </tbody>
  </table>
)

const Phase2ComparisonPanel = () => {
  const [data, setData] = useState<Phase2Comparison | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    adminAPI
      .phase2Comparison()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the Phase 2 comparison')))
    return () => {
      live = false
    }
  }, [])

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" aria-label="Phase 2 vs Phase 1" data-testid="phase2-comparison">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Phase 2 vs Phase 1</h2>
        <span className="text-xs text-gray-400">Admin only</span>
      </div>

      <div className="mt-3">
        <Phase2TrialScoreboard />
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="mt-2 text-sm text-gray-500">Loading…</p>}

      {data && !data.model_exists && (
        <p className="mt-2 text-sm text-gray-600" data-testid="phase2-no-model">
          {NO_MODEL_TEXT}
        </p>
      )}

      {data && data.model_exists && (
        <div className="mt-2 space-y-4 text-sm">
          <p className="text-gray-700" data-testid="phase2-in-charge">
            In charge now: <strong>{WHO[data.in_charge]}</strong> (PHASE2_MODE={data.mode})
          </p>
          {data.size_cap?.capped && (
            <p className="text-amber-800" data-testid="phase2-size-cap">
              Size capped at {+(data.size_cap.cap_fraction * 100).toFixed(2)}% until {data.size_cap.remaining} more model
              signal{data.size_cap.remaining === 1 ? '' : 's'} close.
            </p>
          )}

          {data.overall && data.since && (
            <div>
              <h3 className="font-medium text-gray-800">Since {day(data.since)} (first model-scored setup)</h3>
              <VerdictTable table={data.overall} inCharge={data.periods.length === 1 ? data.periods[0].in_charge : null} testId="phase2-overall" />
            </div>
          )}

          {data.by_direction && (
            <div className="grid gap-4 md:grid-cols-2" data-testid="phase2-by-direction">
              {(['long', 'short'] as const).map((direction) => (
                <div key={direction}>
                  <h3 className="font-medium text-gray-800">{direction === 'long' ? 'Longs only' : 'Shorts only'}</h3>
                  <VerdictTable table={data.by_direction![direction]} inCharge={null} testId={`phase2-${direction}`} />
                </div>
              ))}
              <p className="text-xs text-gray-500 md:col-span-2">
                The same rows split by direction. If a verdict&apos;s keep and reject rows differ overall but not within
                each direction, it was choosing a direction, not better setups.
              </p>
            </div>
          )}

          {data.periods.length > 1 &&
            data.periods.map((p, i) => (
              <div key={p.start} data-testid={`phase2-period-${i}`}>
                <h3 className="font-medium text-gray-800">
                  {day(p.start)} – {p.end ? day(p.end) : 'now'}: {WHO[p.in_charge]} in charge
                </h3>
                <VerdictTable table={p} inCharge={p.in_charge} testId={`phase2-period-${i}-table`} />
              </div>
            ))}

          <p className="rounded-md bg-gray-50 px-3 py-2 text-gray-700" data-testid="phase2-switch-rule">
            {data.switch_rule}
          </p>
          <p className="text-xs text-gray-400">
            Closed signals plus resolved rejected setups, each in both verdicts’ rows. Win rate is gross R &gt; 0; R is
            before fees. Rejected setups are replayed on 1h candles, published signals recorded on 5-minute ticks. A
            row under {data.min_row_n} reads “too few to judge”.
          </p>
        </div>
      )}
    </section>
  )
}

export default Phase2ComparisonPanel
